import { useEffect } from 'react';

/**
 * Focus (and select) a search field from anywhere on the page with ⌘K / Ctrl+K,
 * and clear-then-blur it with Esc while it has focus.
 *
 * Takes a ref to the field's CONTAINER, not the input: `@aimsos/ui`'s
 * `SearchInput` is a plain function component, so it can't receive a ref for
 * its inner `<input>` — the input is looked up inside the container instead.
 *
 * @param {import('react').RefObject<HTMLElement>} containerRef
 * @param {{ onEscape?: () => void }} [options] — `onEscape` clears the query.
 */
export function useCmdK(containerRef, { onEscape } = {}) {
  useEffect(() => {
    const onKeyDown = (e) => {
      const input = containerRef.current?.querySelector('input');
      if (!input) return;

      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        input.focus();
        input.select();
        return;
      }

      // Esc only while the field itself has focus, so it never swallows the
      // key from a modal or any other Esc handler on the page.
      if (e.key === 'Escape' && document.activeElement === input) {
        e.preventDefault();
        onEscape?.();
        input.blur();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [containerRef, onEscape]);
}

export default useCmdK;
